// The Settings form ↔ the API (spec §3 TrainingSettings, §4 PATCH settings/).
// Kept out of the component files (react-refresh) and unit-tested.
//
// The form holds what the person typed — numbers as text, so "62," or a
// cleared field survive until Save — and `buildPatch` turns it back into
// a PATCH that carries only the sections that changed:
//   - flat fields (units, timezone, session_minutes, bodyweight,
//     vertical_pull) only when they differ;
//   - `equipment` and `bands` WHOLE when any part changed (the server
//     replaces each as one value — half a block would be refused);
//   - `priorities` and `target_caps` whole too (each is one stored map);
//   - `seeded_loads` only the keys that changed: the server merges seeds
//     per key, so a new or changed seed is `{load, units}` and a removed
//     one is `null`.
// A form sent back exactly as it was read produces an empty patch (round-
// trip safe), so Save with nothing changed sends nothing.
//
// Errors use the same dotted paths parseApiError gives the server's nested
// errors ("bands.1.assist", "equipment.cable_step", "target_caps.chest",
// "seeded_loads.bench-press.load"), so one lookup places both on the input.

import { parseDecimal } from "@/lib/decimal";
import { formatNumber, MUSCLES } from "../lib/format";
import { convert } from "../lib/steps";

// backend/training/muscles.py EQUIPMENT, in its order, with what each is.
export const EQUIPMENT_ITEMS = [
  { code: "barbell", label: "Barbell and plates" },
  { code: "rack", label: "Squat rack" },
  { code: "bench", label: "Flat bench" },
  { code: "adjustable_bench", label: "Adjustable bench", detail: "Counts as a flat bench too." },
  { code: "dumbbells", label: "Dumbbells" },
  { code: "cable", label: "Cable station", detail: "A functional trainer or cable stack." },
  { code: "pullup_bar", label: "Pull-up bar" },
  { code: "bands", label: "Resistance bands" },
];

// The serializers' bounds (backend/training/serializers.py, constants.py).
export const MINUTES = { min: 20, max: 120, step: 5 };
export const MAX_BANDS = 8;
export const BAND_NAME_MAX = 40;
export const MAX_ASSIST = 300;
export const MAX_DUMBBELL = 500;
export const STEP_BOUNDS = { min: 0.25, max: 50 };
export const MAX_BODYWEIGHT = 700;
export const MAX_SEED_LOAD = 2000;
export const MAX_CAP = 40;

export const DEFAULT_PRIORITY = "grow";
export const PRIORITY_OPTIONS = [
  { value: "emphasize", label: "Emphasize" },
  { value: "grow", label: "Grow" },
  { value: "maintain", label: "Maintain" },
];

export const VERTICAL_PULLS = [
  { value: "chin_up", label: "Chin-up", detail: "Bodyweight, with a band or added weight" },
  { value: "lat_pulldown", label: "Lat pulldown", detail: "On the cable station" },
];

export const UNIT_OPTIONS = [
  { value: "lb", label: "Pounds (lb)" },
  { value: "kg", label: "Kilograms (kg)" },
];

const STEP_FIELDS = ["dumbbell_step", "barbell_step", "cable_step"];
const EQUIPMENT_NUMBERS = ["dumbbell_max", ...STEP_FIELDS];
const MUSCLE_CODES = new Set(MUSCLES.map((m) => m.code));

// The order sections are named in ("Saved your units and equipment.").
export const SECTION_NAMES = {
  units: "units",
  timezone: "time zone",
  session_minutes: "session length",
  equipment: "equipment",
  bands: "bands",
  bodyweight: "bodyweight",
  vertical_pull: "vertical pull",
  priorities: "priorities",
  target_caps: "weekly caps",
  seeded_loads: "seeds",
};

const text = (value) =>
  value === null || value === undefined || value === "" ? "" : formatNumber(value);

const near = (a, b) =>
  a === null || b === null || a === undefined || b === undefined
    ? a === b
    : Math.abs(a - b) < 0.005;

let bandIds = 0;
/** A fresh local key for a band row (React keys survive removals). */
export const newBandRow = (name = "", assist = "") => ({
  id: `band-${(bandIds += 1)}`,
  name,
  assist,
});

/** The form's starting state from GET/PATCH settings/ (missing parts tolerated). */
export function toForm(settings) {
  const s = settings || {};
  const equipment = s.equipment || {};
  const units = s.units ?? "lb";
  return {
    units,
    timezone: s.timezone ?? "",
    session_minutes: text(s.session_minutes),
    equipment: {
      units: equipment.units ?? units,
      items: [...(equipment.items ?? [])],
      dumbbell_max: text(equipment.dumbbell_max),
      dumbbell_step: text(equipment.dumbbell_step),
      barbell_step: text(equipment.barbell_step),
      cable_step: text(equipment.cable_step),
    },
    bands: (s.bands ?? []).map((band) => newBandRow(band?.name ?? "", text(band?.assist))),
    // The typed bodyweight once the person has touched the field; until
    // then the field shows the saved one (converted when the units switch).
    bodyweight: text(s.bodyweight),
    bodyweightTouched: false,
    vertical_pull: s.vertical_pull ?? "chin_up",
    priorities: Object.fromEntries(
      MUSCLES.map((m) => [m.code, s.priorities?.[m.code] ?? DEFAULT_PRIORITY]),
    ),
    target_caps: Object.fromEntries(MUSCLES.map((m) => [m.code, text(s.target_caps?.[m.code])])),
    seeds: Object.entries(s.seeded_loads ?? {})
      .filter(([, seed]) => seed)
      .map(([key, seed]) => ({
        key,
        load: text(seed.load),
        units: seed.units ?? units,
        set_at: seed.set_at ?? null,
      })),
  };
}

/**
 * What the bodyweight field shows: the typed text once touched; else the
 * saved bodyweight — converted to the units the form now has, because the
 * server converts it on a switch (services.update_settings).
 */
export function shownBodyweight(settings, form) {
  if (form.bodyweightTouched) return form.bodyweight;
  const saved = settings?.bodyweight;
  if (saved === null || saved === undefined || saved === "") return "";
  const from = settings?.units ?? "lb";
  return text(convert(Number(saved), from, form.units));
}

const numberError = (value, { min, max, integer = false, decimals }) => {
  if (value === null) return "nan";
  if (integer && !Number.isInteger(value)) return "range";
  if (value < min || value > max) return "range";
  if (decimals !== undefined && Math.abs(value * 10 ** decimals - Math.round(value * 10 ** decimals)) > 1e-6) {
    return "decimals";
  }
  return null;
};

/** Whether typed number text differs from the saved one ("5.0" is still 5; junk always differs). */
function numberChanged(typed, before) {
  const t = typed.trim();
  if (t === before) return false;
  const value = t === "" ? null : parseDecimal(t);
  if (t !== "" && value === null) return true;
  return !near(value, before === "" ? null : parseDecimal(before));
}

/**
 * The PATCH for what changed since `settings`, the section names it
 * touches (in page order), and the errors that stop it — keyed by the
 * same dotted paths as the server's. Only changed sections are checked:
 * an untouched one is sent as nothing, whatever it holds.
 */
export function buildPatch(settings, form) {
  const base = toForm(settings);
  const patch = {};
  const errors = {};
  const touched = [];
  const units = form.units;

  // --- units, time zone, session length ---------------------------------
  if (form.units !== base.units) {
    patch.units = form.units;
    touched.push("units");
  }
  const zone = form.timezone.trim();
  if (zone !== base.timezone) {
    touched.push("timezone");
    if (!zone) errors.timezone = "Pick your time zone, like America/Denver.";
    else patch.timezone = zone;
  }
  const minutes = parseDecimal(form.session_minutes);
  if (numberChanged(form.session_minutes, base.session_minutes)) {
    touched.push("session_minutes");
    if (numberError(minutes, { min: MINUTES.min, max: MINUTES.max, integer: true })) {
      errors.session_minutes = `Type a whole number of minutes from ${MINUTES.min} to ${MINUTES.max}.`;
    } else {
      patch.session_minutes = minutes;
    }
  }

  // --- equipment (sent whole) --------------------------------------------
  const eq = form.equipment;
  const eqNumbers = Object.fromEntries(EQUIPMENT_NUMBERS.map((f) => [f, parseDecimal(eq[f])]));
  const items = EQUIPMENT_ITEMS.map((i) => i.code).filter((c) => eq.items.includes(c));
  const extraItems = eq.items.filter((c) => !EQUIPMENT_ITEMS.some((i) => i.code === c));
  const allItems = [...items, ...extraItems];
  const baseItems = new Set(base.equipment.items);
  const equipmentChanged =
    eq.units !== base.equipment.units ||
    allItems.length !== baseItems.size ||
    allItems.some((c) => !baseItems.has(c)) ||
    EQUIPMENT_NUMBERS.some((f) => numberChanged(eq[f], base.equipment[f]));
  if (equipmentChanged) {
    touched.push("equipment");
    const u = eq.units;
    if (numberError(eqNumbers.dumbbell_max, { min: 0, max: MAX_DUMBBELL })) {
      errors["equipment.dumbbell_max"] =
        `Type your heaviest dumbbell in ${u}, from 0 to ${MAX_DUMBBELL} — like ${u === "kg" ? "40" : "90"}.`;
    }
    for (const field of STEP_FIELDS) {
      if (numberError(eqNumbers[field], STEP_BOUNDS)) {
        errors[`equipment.${field}`] =
          `Type a step from ${STEP_BOUNDS.min} to ${STEP_BOUNDS.max} ${u}, like ${u === "kg" ? "2.5" : "5"}.`;
      }
    }
    patch.equipment = { units: eq.units, items: allItems, ...eqNumbers };
  }

  // --- bands (one list, replaced whole) ----------------------------------
  const bandsChanged =
    form.bands.length !== base.bands.length ||
    form.bands.some(
      (band, i) =>
        band.name !== base.bands[i].name || numberChanged(band.assist, base.bands[i].assist),
    );
  if (bandsChanged) {
    touched.push("bands");
    const assistUnits = eq.units;
    const seen = new Set();
    form.bands.forEach((band, i) => {
      const name = band.name.trim();
      if (!name) errors[`bands.${i}.name`] = "Name the band, like light or green.";
      else if (name.length > BAND_NAME_MAX) {
        errors[`bands.${i}.name`] = `Keep the name to ${BAND_NAME_MAX} characters.`;
      } else if (seen.has(name.toLowerCase())) {
        errors[`bands.${i}.name`] = "Another band has this name — give each band a different one.";
      }
      seen.add(name.toLowerCase());
      if (numberError(parseDecimal(band.assist), { min: 0, max: MAX_ASSIST })) {
        errors[`bands.${i}.assist`] =
          `Type the assist in ${assistUnits}, from 0 to ${MAX_ASSIST} — like ${assistUnits === "kg" ? "15" : "30"}.`;
      }
    });
    if (form.bands.length > MAX_BANDS) errors.bands = `Up to ${MAX_BANDS} bands — remove one.`;
    patch.bands = form.bands.map((band) => ({
      name: band.name.trim(),
      assist: parseDecimal(band.assist),
    }));
  }

  // --- bodyweight (in the settings' units; converts itself on a switch) --
  if (form.bodyweightTouched) {
    const typed = form.bodyweight.trim();
    const value = typed === "" ? null : parseDecimal(typed);
    const before = parseDecimal(shownBodyweight(settings, { ...form, bodyweightTouched: false }));
    if (typed !== "" && value === null) {
      touched.push("bodyweight");
      errors.bodyweight = `Type your bodyweight in ${units}, like ${units === "kg" ? "82.5" : "182.5"} — or leave it blank.`;
    } else if (!near(value, before)) {
      touched.push("bodyweight");
      const problem =
        value === null ? null : numberError(value, { min: 0, max: MAX_BODYWEIGHT, decimals: 2 });
      if (problem === "decimals") errors.bodyweight = "Use at most two decimals, like 182.25.";
      else if (problem) {
        errors.bodyweight = `Type your bodyweight in ${units}, from 0 to ${MAX_BODYWEIGHT} — or leave it blank.`;
      } else patch.bodyweight = value;
    }
  }

  // --- vertical pull ------------------------------------------------------
  if (form.vertical_pull !== base.vertical_pull) {
    patch.vertical_pull = form.vertical_pull;
    touched.push("vertical_pull");
  }

  // --- priorities (one map; a muscle left out means grow) ----------------
  const savedPriorities = settings?.priorities ?? {};
  if (MUSCLES.some((m) => form.priorities[m.code] !== base.priorities[m.code])) {
    touched.push("priorities");
    const out = {};
    for (const [code, value] of Object.entries(savedPriorities)) {
      if (!MUSCLE_CODES.has(code)) out[code] = value; // a muscle this page doesn't know: kept
    }
    for (const m of MUSCLES) {
      const value = form.priorities[m.code];
      if (value !== DEFAULT_PRIORITY || savedPriorities[m.code] !== undefined) out[m.code] = value;
    }
    patch.priorities = out;
  }

  // --- weekly caps (one map; blank = no cap) ------------------------------
  const savedCaps = settings?.target_caps ?? {};
  if (MUSCLES.some((m) => numberChanged(form.target_caps[m.code], base.target_caps[m.code]))) {
    touched.push("target_caps");
    const out = {};
    for (const [code, value] of Object.entries(savedCaps)) {
      if (!MUSCLE_CODES.has(code)) out[code] = value;
    }
    for (const m of MUSCLES) {
      const typed = form.target_caps[m.code].trim();
      if (typed === "") continue;
      const value = parseDecimal(typed);
      if (numberError(value, { min: 0, max: MAX_CAP, integer: true })) {
        errors[`target_caps.${m.code}`] =
          `A whole number of sets from 0 to ${MAX_CAP} — or blank for no cap.`;
      } else out[m.code] = value;
    }
    patch.target_caps = out;
  }

  // --- seeds (merged per key: send only what changed; null removes) -------
  const savedSeeds = settings?.seeded_loads ?? {};
  const seeds = {};
  for (const [key, seed] of Object.entries(savedSeeds)) {
    if (seed && !form.seeds.some((row) => row.key === key)) seeds[key] = null;
  }
  for (const row of form.seeds) {
    const load = parseDecimal(row.load);
    const was = savedSeeds[row.key];
    if (was && row.units === was.units && !numberChanged(row.load, text(was.load))) continue;
    if (numberError(load, { min: 0, max: MAX_SEED_LOAD })) {
      errors[`seeded_loads.${row.key}.load`] =
        `Type the load in ${row.units}, from 0 to ${MAX_SEED_LOAD}.`;
    }
    seeds[row.key] = { load, units: row.units };
  }
  if (Object.keys(seeds).length) {
    touched.push("seeded_loads");
    patch.seeded_loads = seeds;
  }

  return { patch, sections: touched, errors };
}

/** The form's value at an error's dotted path (seeds addressed by key). */
export function valueAt(form, path) {
  const [head, ...rest] = path.split(".");
  let node =
    head === "seeded_loads"
      ? Object.fromEntries(form.seeds.map((row) => [row.key, row]))
      : form[head];
  for (const part of rest) {
    if (node === null || node === undefined) return undefined;
    node = node[part];
  }
  return node;
}

/** True while the value at `path` is still what it was in `before`. */
export const sameAt = (before, after, path) =>
  JSON.stringify(valueAt(before, path)) === JSON.stringify(valueAt(after, path));

/**
 * The path a server error is shown at: list items of `equipment.items`
 * go to the items group; a seed's own error goes to its load field.
 */
export function placePath(path) {
  const parts = path.split(".");
  if (parts[0] === "equipment" && parts[1] === "items") return "equipment.items";
  if (parts[0] === "seeded_loads" && parts.length === 2) return `${path}.load`;
  return path;
}

const PLACED = new Set([
  "units",
  "timezone",
  "session_minutes",
  "bodyweight",
  "vertical_pull",
  "equipment",
  "equipment.units",
  "equipment.items",
  ...EQUIPMENT_NUMBERS.map((f) => `equipment.${f}`),
  "bands",
  "priorities",
  "target_caps",
  "seeded_loads",
]);

/** Whether the page has an input (or a section line) for this error path. */
export function isPlaced(path, form) {
  if (PLACED.has(path)) return true;
  const [head, key, field] = path.split(".");
  if (head === "bands") {
    return Number(key) < form.bands.length && ["name", "assist"].includes(field);
  }
  if (head === "priorities" || head === "target_caps") {
    return field === undefined && MUSCLE_CODES.has(key);
  }
  if (head === "seeded_loads") {
    return form.seeds.some((row) => row.key === key) && ["load", "units"].includes(field);
  }
  return false;
}

/** "a", "a and b", "a, b and c". */
export function listWords(words) {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** The section names a patch touched, as words in page order. */
export const sectionWords = (sections) =>
  Object.keys(SECTION_NAMES)
    .filter((key) => sections.includes(key))
    .map((key) => SECTION_NAMES[key]);

/**
 * What a successful save says: what was saved, what happens next, and the
 * bodyweight the server converted on a units switch.
 */
export function savedMessage(sections, patch, saved) {
  const words = sectionWords(sections);
  // "bodyweight" is one thing, "priorities" or "units and bands" several.
  const one = words.length === 1 && !/s$/.test(words[0]);
  let message = `Saved your ${listWords(words)}. New plans use ${one ? "it" : "them"} from now on.`;
  if (
    patch.units &&
    !("bodyweight" in patch) &&
    saved?.bodyweight !== null &&
    saved?.bodyweight !== undefined
  ) {
    message += ` Your bodyweight is now ${formatNumber(saved.bodyweight)} ${saved.units}.`;
  }
  return message;
}

// --- time zones ------------------------------------------------------------

/** This device's IANA zone, or "" when the browser won't say. */
export function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

/**
 * The zones to pick from, grouped by area (`[{area, zones}]`), with UTC
 * and the current value always present — or null when the browser can't
 * list them (no Intl.supportedValuesOf): the page asks for a typed name.
 */
export function timeZoneGroups(current = "") {
  let zones;
  try {
    if (typeof Intl.supportedValuesOf !== "function") return null;
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    return null;
  }
  if (!Array.isArray(zones) || zones.length === 0) return null;
  const all = new Set(zones);
  all.add("UTC");
  if (current) all.add(current);
  const groups = new Map();
  for (const zone of [...all].sort()) {
    const area = zone.includes("/") ? zone.split("/")[0] : "Other";
    if (!groups.has(area)) groups.set(area, []);
    groups.get(area).push(zone);
  }
  return [...groups.entries()].map(([area, list]) => ({ area, zones: list }));
}

/** "America/Argentina/Buenos_Aires" → "America/Argentina/Buenos Aires". */
export const zoneLabel = (zone) => zone.replace(/_/g, " ");

/** The DOM id of the input an error path lands on. */
export const fieldId = (path) => `training-settings-${path.replace(/[^A-Za-z0-9_-]/g, "-")}`;
